import { connectRefinementList } from "instantsearch.js/es/connectors";
import { curatedFacetRefinementsSpacer, facetNoResultsLabel, isCuratedLayoutPage } from "../curated-layout";
import { escapeHtml, runAfterFrame } from "./helpers";
import {
    modelFamilyFacetFetchLimit,
    familyMatchesQuery,
    familyToggleValues,
    groupModelFacetItems,
    modelFamilies,
    toggleFacetValuesOnce,
} from "../model-families";

type SortByDirection =
    | "count:asc"
    | "count:desc"
    | "name:asc"
    | "name:desc"
    | "isRefined";

export interface MenuStyleRefinementOptions {
    placeholder?: string;
    searchable?: boolean;
    showMore?: boolean;
    showMoreLimit?: number;
    limit?: number;
    sortBy?: SortByDirection[];
    /** true → square checkbox (AND operator), false → rounded radio (OR) */
    multiple?: boolean;
    operator?: "and" | "or";
    transformItems?: (items: any[]) => any[];
    /**
     * Facette `model` : une ligne par famille de modèles (« Cayenne » pour Cayenne II et III), qui
     * choisit ou retire toutes ses désignations (`model-families.ts`). Le filtre reste la désignation.
     */
    groupByModelFamily?: boolean;
}

/** Valeurs choisies de la facette dans l'état de recherche (y compris celles absentes de la liste). */
function refinedValuesOf(renderOptions: unknown, attribute: string): string[] {
    const state = (
        renderOptions as {
            instantSearchInstance?: {
                mainIndex?: { getHelper?: () => { state?: Record<string, Record<string, string[]> | undefined> } | null };
            };
        }
    ).instantSearchInstance?.mainIndex?.getHelper?.()?.state;

    return [
        ...(state?.disjunctiveFacetsRefinements?.[attribute] ?? []),
        ...(state?.facetsRefinements?.[attribute] ?? []),
    ];
}

function resolveFacetContainers(selector: string | string[]): HTMLElement[] {
    const selectors = Array.isArray(selector) ? selector : [selector];
    return selectors.flatMap((sel) => {
        const el = document.querySelector<HTMLElement>(sel);
        return el ? [el] : [];
    });
}

/**
 * Custom refinement list that outputs the same HTML as Vue's Menu.vue
 * (RefinementList__* classes, rounded or square checkboxes, has-selected dimming).
 * Used for model and options so Blade and Vue share the same DOM structure.
 */
export function createMenuStyleRefinementWidget(
    containerSelector: string | string[],
    attribute: string,
    opts: MenuStyleRefinementOptions = {},
) {
    const containers = resolveFacetContainers(containerSelector);
    if (!containers.length) return null;

    const {
        placeholder = "Rechercher…",
        searchable = false,
        showMore = false,
        showMoreLimit = 20,
        limit = 10,
        sortBy,
        multiple = false,
        operator = "or",
        transformItems,
        groupByModelFamily: groupRequested = false,
    } = opts;
    // Sans famille renseignée, la facette reste exactement celle d'avant (aucun regroupement)
    const groupByModelFamily = groupRequested && Object.keys(modelFamilies()).length > 0;
    // Regroupée : toutes les valeurs sont demandées (une famille additionne des générations moins
    // fournies), « Voir plus » coupe ensuite la liste des familles à `limit`
    let showAllFamilies = false;

    let latestRefine: ((value: string) => void) | null = null;
    let latestInstance: Parameters<typeof toggleFacetValuesOnce>[0] = null;
    // Famille de modèles : désignations choisies et connues au dernier rendu, pour la basculer en entier
    let latestRefinedValues: string[] = [];
    let latestKnownValues: string[] = [];
    const toggleValue = (value: string): void => {
        if (!latestRefine) return;
        if (!groupByModelFamily) {
            latestRefine(value);
            return;
        }
        // Toutes les désignations de la famille en un seul changement d'état (une recherche, une navigation)
        toggleFacetValuesOnce(
            latestInstance,
            attribute,
            familyToggleValues(value, latestRefinedValues, latestKnownValues),
            (designation) => latestRefine?.(designation),
        );
    };
    // Curated page, model facet: Menu.vue « Tout sélectionner », shown once the visitor searched.
    const withSelectAll = attribute === "model" && isCuratedLayoutPage();
    let latestDisplayValues: string[] = [];
    let listenersBound = false;
    let localSearchQuery = "";

    const scheduleFacetRender = (
        renderOptions: Parameters<
            Parameters<typeof connectRefinementList>[0]
        >[0],
    ) => {
        (
            renderOptions as typeof renderOptions & {
                instantSearchInstance: { scheduleRender: () => void };
            }
        ).instantSearchInstance.scheduleRender();
    };

    const customRefinement = connectRefinementList<{ container: string }>(
        (renderOptions, isFirstRender) => {
            const {
                items: rawItems,
                refine,
                searchForItems,
                isShowingMore,
                canToggleShowMore,
                toggleShowMore,
            } = renderOptions;
            latestRefine = refine;
            latestInstance = (renderOptions as typeof renderOptions & {
                instantSearchInstance?: Parameters<typeof toggleFacetValuesOnce>[0];
            }).instantSearchInstance;

            const transformed = transformItems ? transformItems(rawItems) : rawItems;
            if (groupByModelFamily) {
                latestRefinedValues = refinedValuesOf(renderOptions, attribute);
                latestKnownValues = rawItems.map((item) => item.value);
            }
            const groupedItems = groupByModelFamily
                ? groupModelFacetItems(transformed.map((item) => ({ ...item })), { resort: true })
                : transformed;
            const familyShowMore = groupByModelFamily && showMore && groupedItems.length > limit;
            // Regroupée : toutes désignations additionnées, puis la liste
            // des familles coupée comme l'ancienne liste (`limit`, `showMoreLimit` avec « Voir plus »)
            const familyDisplayCap = familyShowMore && !showAllFamilies ? limit : Math.max(limit, showMoreLimit);
            const items =
                groupByModelFamily && localSearchQuery.trim() === ""
                    ? groupedItems.slice(0, familyDisplayCap)
                    : groupedItems;
            const hasSelected = items.some((i) => i.isRefined);

            const q = localSearchQuery.trim().toLowerCase();
            // Regroupée : la recherche porte sur le nom de la famille et sur ses désignations
            const displayItems = q
                ? items.filter((item) =>
                      groupByModelFamily
                          ? familyMatchesQuery(item.value, q, latestKnownValues)
                          : item.label.toLowerCase().includes(q),
                  )
                : items;

            latestDisplayValues = displayItems.map((item) => item.value);

            const listHtml = displayItems.length
                ? displayItems
                      .map(
                          (item) =>
                              `<label data-attr="listing-panel-option" class="RefinementList${
                                  item.isRefined
                                      ? " RefinementList--selected"
                                      : ""
                              }">` +
                              `<span><span class="RefinementList__labelText">${escapeHtml(
                                  item.label,
                              )}</span>` +
                              `<span class="RefinementList__count">(${item.count.toLocaleString(
                                  "fr-FR",
                              )})</span></span>` +
                              `<input type="checkbox" class="RefinementList__checkbox${
                                  !multiple
                                      ? " RefinementList__checkbox--rounded"
                                      : ""
                              }"` +
                              `${
                                  item.isRefined ? " checked" : ""
                              } data-value="${escapeHtml(
                                  item.value,
                              )}" /></label>`,
                      )
                      .join("")
                : `<span class="no-results-label">${facetNoResultsLabel()}</span>`;

            const showMoreToggle = groupByModelFamily ? familyShowMore : canToggleShowMore;
            const showingMore = groupByModelFamily ? showAllFamilies : isShowingMore;
            const showMoreHtml = showMoreToggle
                ? `<button type="button" class="RefinementList__showMore" data-attr="listing-panel-show-more">${
                      showingMore ? "Voir moins" : "Voir plus"
                  }</button>`
                : "";

            const markup =
                (searchable
                    ? `<div class="RefinementList__search input__field input__field--facette"><input type="search" class="input input--search" data-attr="listing-panel-search" placeholder="${escapeHtml(
                          placeholder,
                      )}" autocomplete="off" data-facet-search /></div>`
                    : "") +
                curatedFacetRefinementsSpacer(attribute) +
                (withSelectAll
                    ? `<button type="button" class="select-all-button" data-curated-select-all hidden>Tout sélectionner</button>`
                    : "") +
                `<div class="RefinementList__wrapper${
                    hasSelected && !multiple
                        ? " RefinementList__wrapper--has-selected"
                        : ""
                }">${listHtml}${showMoreHtml}</div>`;

            for (const container of containers) {
                if (isFirstRender) {
                    container.innerHTML = markup;
                } else {
                    const wrap = container.querySelector<HTMLElement>(
                        ".RefinementList__wrapper",
                    );
                    if (wrap) {
                        wrap.classList.toggle(
                            "RefinementList__wrapper--has-selected",
                            hasSelected && !multiple,
                        );
                        wrap.innerHTML = listHtml + showMoreHtml;
                    }
                }
            }

            if (!listenersBound && isFirstRender) {
                listenersBound = true;

                // Bind search on every mount (sidebar + action-bar). Listening
                // only on containers[0] left the action-bar input inert when it
                // was the secondary selector — which is the one users type in.
                if (searchable) {
                    const onFacetSearchInput = (event: Event) => {
                        const input = event.target as HTMLInputElement;
                        if (
                            !(input instanceof HTMLInputElement) ||
                            !input.matches("[data-facet-search]")
                        ) {
                            return;
                        }
                        localSearchQuery = input.value;
                        containers.forEach((container) => {
                            container
                                .querySelector<HTMLElement>("[data-curated-select-all]")
                                ?.removeAttribute("hidden");
                            const mirror =
                                container.querySelector<HTMLInputElement>(
                                    "[data-facet-search]",
                                );
                            if (mirror && mirror !== input) {
                                mirror.value = input.value;
                            }
                        });
                        // Regroupée : toutes les valeurs sont déjà là, la recherche est locale (familles
                        // et désignations) ; la recherche distante ne connaît que les désignations
                        if (!groupByModelFamily) searchForItems(input.value);
                        scheduleFacetRender(renderOptions);
                    };

                    containers.forEach((container) => {
                        container.addEventListener("input", onFacetSearchInput);
                    });
                }

                // Checkboxes refine on `change` only (a label/checkbox tap
                // always fires it) and defer the InstantSearch work past the
                // next paint so the tap's INP window stays short. `click`
                // must not also refine: with the deferred refine the input
                // stays in the DOM, so both events would fire and the two
                // toggles would cancel each other.
                const onShowMoreClick = (event: Event) => {
                    const target = event.target as HTMLElement;
                    if (withSelectAll && target.matches("[data-curated-select-all]")) {
                        const values = [...latestDisplayValues];
                        target.setAttribute("hidden", "");
                        runAfterFrame(() => values.forEach((value) => toggleValue(value)));
                        return;
                    }
                    if (target.classList.contains("RefinementList__showMore")) {
                        if (groupByModelFamily) {
                            showAllFamilies = !showAllFamilies;
                            runAfterFrame(() => scheduleFacetRender(renderOptions));
                            return;
                        }
                        runAfterFrame(() => toggleShowMore());
                    }
                };
                const onCheckboxChange = (event: Event) => {
                    const input = event.target as HTMLInputElement;
                    if (!input.classList.contains("RefinementList__checkbox")) {
                        return;
                    }
                    const val = input.dataset.value;
                    if (val) runAfterFrame(() => toggleValue(val));
                };

                containers.forEach((container) => {
                    container.addEventListener("change", onCheckboxChange);
                    container.addEventListener("click", onShowMoreClick);
                });
            }
        },
    );

    return customRefinement({
        container: containers[0].id
            ? `#${containers[0].id}`
            : (Array.isArray(containerSelector)
                  ? containerSelector[0]
                  : containerSelector),
        attribute,
        operator,
        // Regroupée : toutes les valeurs d'un coup (le « Voir plus » est local, sur les familles)
        limit: groupByModelFamily ? Math.max(limit, showMoreLimit, modelFamilyFacetFetchLimit()) : limit,
        showMore: groupByModelFamily ? false : showMore,
        showMoreLimit,
        ...(sortBy ? { sortBy } : {}),
    });
}
